import React, { useMemo } from 'react';
import { useSelector } from "react-redux";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { convertCurrencyHelper } from '../../../helpers/forexConvertor';

// Skeleton loader component for the line chart
const LineChartSkeletonLoader = () => {
  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  
  // Fixed Y positions for line points to avoid random values
  const currentYearPoints = [65, 55, 45, 60, 50, 58, 70, 48, 62, 68, 72, 64];
  const lastYearPoints = [40, 35, 30, 55, 60, 52, 58, 56, 32, 45, 33, 46];
  
  return (
    <div style={{ width: '100%', height: 542 }}>
      <div style={{ 
        width: '100%', 
        height: '100%', 
        padding: '20px 30px 5px 50px',
        position: 'relative'
      }}>
        {/* Y-axis skeleton labels */}
        <div style={{ 
          position: 'absolute', 
          left: '0', 
          top: '20px', 
          bottom: '5px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          paddingRight: '10px',
          width: '50px'
        }}>
          {Array.from({ length: 5 }).map((_, index) => (
            <div
              key={index}
              style={{
                width: '40px',
                height: '12px',
                backgroundColor: '#e5e7eb',
                borderRadius: '4px',
                animation: 'pulse 1.5s ease-in-out infinite',
              }}
            />
          ))}
        </div>

        {/* Chart area */}
        <div style={{ 
          marginLeft: '50px',
          height: 'calc(100% - 25px)',
          marginTop: '20px',
          marginBottom: '5px',
          position: 'relative'
        }}>
          {/* Grid lines skeleton */}
          <div style={{ 
            position: 'absolute',
            top: '0',
            left: '0',
            right: '30px',
            bottom: '0',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between'
          }}>
            {Array.from({ length: 5 }).map((_, index) => (
              <div
                key={index}
                style={{
                  width: '100%',
                  height: '1px',
                  background: 'repeating-linear-gradient(to right, #D6D9DB 0px, #D6D9DB 3px, transparent 3px, transparent 6px)',
                  opacity: 0.3
                }}
              />
            ))}
          </div>

          {/* Vertical grid lines */}
          <div style={{ 
            position: 'absolute',
            top: '0',
            left: '0',
            right: '30px',
            bottom: '0',
            display: 'flex',
            justifyContent: 'space-between',
            paddingLeft: '50px',
            paddingRight: '0'
          }}>
            {months.map((_, index) => (
              <div
                key={index}
                style={{
                  width: '1px',
                  height: '100%',
                  background: 'repeating-linear-gradient(to bottom, #D6D9DB 0px, #D6D9DB 3px, transparent 3px, transparent 6px)',
                  opacity: 0.3
                }}
              />
            ))}
          </div>

          {/* Line paths skeleton */}
          <svg 
            style={{ width: '100%', height: '100%', position: 'absolute', top: 0, left: 0 }}
            viewBox="0 0 1000 500"
            preserveAspectRatio="none"
          >
            {/* Current Year line skeleton */}
            <polyline
              points={currentYearPoints.map((yPercent, index) => {
                const x = (index / (months.length - 1)) * 1000;
                const y = (yPercent / 100) * 500;
                return `${x},${y}`;
              }).join(' ')}
              fill="none"
              stroke="#96A8BA"
              strokeWidth="2"
              strokeDasharray="5,5"
              opacity="0.3"
              style={{ animation: 'pulse 1.5s ease-in-out infinite' }}
            />
            {/* Last Year line skeleton */}
            <polyline
              points={lastYearPoints.map((yPercent, index) => {
                const x = (index / (months.length - 1)) * 1000;
                const y = (yPercent / 100) * 500;
                return `${x},${y}`;
              }).join(' ')}
              fill="none"
              stroke="#6479F8"
              strokeWidth="2"
              strokeDasharray="5,5"
              opacity="0.3"
              style={{ animation: 'pulse 1.5s ease-in-out infinite', animationDelay: '0.2s' }}
            />
          </svg>
        </div>

        {/* X-axis skeleton ticks */}
        <div style={{ 
          position: 'absolute', 
          bottom: '5px', 
          left: '50px', 
          right: '30px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          paddingLeft: '50px'
        }}>
          {months.map((_, index) => (
            <div
              key={index}
              style={{
                width: '45px',
                height: '12px',
                backgroundColor: '#e5e7eb',
                borderRadius: '4px',
                animation: 'pulse 1.5s ease-in-out infinite',
                flexShrink: 0
              }}
            />
          ))}
        </div>

        {/* Legend skeleton - positioned at top right */}
        <div style={{ 
          position: 'absolute',
          top: '0',
          right: '30px',
          display: 'flex',
          gap: '20px',
          alignItems: 'center'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{
              width: '20px',
              height: '2px',
              backgroundColor: '#96A8BA',
              opacity: 0.3,
              animation: 'pulse 1.5s ease-in-out infinite',
            }} />
            <div style={{
              width: '100px',
              height: '12px',
              backgroundColor: '#e5e7eb',
              borderRadius: '4px',
              animation: 'pulse 1.5s ease-in-out infinite',
            }} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{
              width: '20px',
              height: '2px',
              backgroundColor: '#6479F8',
              opacity: 0.3,
              animation: 'pulse 1.5s ease-in-out infinite',
            }} />
            <div style={{
              width: '100px',
              height: '12px',
              backgroundColor: '#e5e7eb',
              borderRadius: '4px',
              animation: 'pulse 1.5s ease-in-out infinite',
            }} />
          </div>
        </div>
      </div>
      <style>{`
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
      `}</style>
    </div>
  );
};

const useProcessedData = (clientInvoices, clientInvoicesLoading, isError, reportingOverview, preferredCurrency, forex) => {
  return useMemo(() => {
    if (clientInvoicesLoading || isError) return [];

    const currentYear = reportingOverview.year || new Date().getFullYear();
    const previousYear = currentYear - 1;

    const calculateROI = (invoices, year) => {
      const monthlyData = Array.from({ length: 12 }, (_, i) => ({
        name: new Date(year, i).toLocaleString('default', { month: 'long' }),
        budget: 0,
        cost: 0
      }));

      // projects.forEach(project => {
      //   const projectYear = new Date(project.startTime).getFullYear();
      //   const preferredClientCurrency = project?.clientId?.preferred_currency || 'USD';
      //   if (projectYear === year && !project?.project?.is_delete && reportingOverview.project.includes(project?.project?._id) && reportingOverview.currency.includes(project?.clientId?.preferred_currency || 'USD')) {
      //     const monthIndex = new Date(project.startTime).getMonth();
      //     monthlyData[monthIndex].budget += convertCurrencyHelper(preferredClientCurrency, preferredCurrency, project.project.budget, forex);
      //     const userCost = project.user.cost || 0;
      //     const userOverheadCost = project.user.overhead_cost || 0;
      //     const userTypeOfContract = project.user.typeOfContract || typeOfContract.Hourly_Rate;
      //     const paymentCurrency = project.user.payment_currency || 'USD';
      //     let finalCost;
      //     if (userTypeOfContract === typeOfContract.Hourly_Rate) {
      //       finalCost = userCost;
      //     } else {
      //       finalCost = userCost / 168; // Assuming a different type of contract with monthly rate divided by 168 hours
      //     }
      //     // const cost = (userCost + userOverheadCost) * (project.duration / 3600);
      //     const cost = (finalCost * (project.duration / 3600)) + (userOverheadCost * (project.duration / 3600));
      //     monthlyData[monthIndex].cost += convertCurrencyHelper(paymentCurrency, preferredCurrency, cost, forex);
      //   }
      // });

      invoices.forEach(invoice => {
        const projectYear = new Date(invoice.cli_inv_end_date).getFullYear();
        const preferredClientCurrency = invoice?.cli_inv_currency || 'USD';
        const clientId = invoice.cli_inv_for && invoice.cli_inv_for?._id;
        if (projectYear === year && !invoice.is_delete && clientId && (reportingOverview.client ? reportingOverview.client.includes(clientId) : true) && (reportingOverview.currency && reportingOverview.currency.includes(preferredClientCurrency))) {
          const monthIndex = new Date(invoice.cli_inv_end_date).getMonth();
          const paymentCurrency = invoice.cli_inv_currency || 'USD';
          monthlyData[monthIndex].cost += convertCurrencyHelper(paymentCurrency, preferredCurrency, invoice.cli_inv_total_amount, forex);
        }
      });
      return monthlyData.map(month => ({
        name: month.name,
        roi: month.cost ? (month.cost).toFixed(2) : 0
      }));
    };

    const currentYearData = calculateROI(clientInvoices, currentYear);
    const previousYearData = calculateROI(clientInvoices, previousYear);

    return currentYearData.map((data, index) => ({
      name: data.name,
      currentYear: parseFloat(data.roi),
      lastYear: parseFloat(previousYearData[index].roi)
    }));
  }, [clientInvoices, clientInvoicesLoading, isError, reportingOverview, preferredCurrency, forex]);
};

const ReportClientComparionGraph = ({ clientInvoices, clientInvoicesLoading, clientInvoicesError }) => {
  const reportingOverview = useSelector((state) => state.reportingOverview);
  const { forex } = useSelector((state) => state.forex);
  const settings = JSON.parse(localStorage.getItem("settings"));

  const setReportingCurrency = useSelector((state) => state.reportingOverview.currency);
  var currencies = "USD";
  if (setReportingCurrency && setReportingCurrency.length === 1) {
    currencies = setReportingCurrency[0];
  } else {
    currencies = settings.currency;
  }

  const processedData = useProcessedData(clientInvoices, clientInvoicesLoading, clientInvoicesError, reportingOverview, currencies, forex);

  const currentYear = reportingOverview.year || new Date().getFullYear();
  const previousYear = currentYear - 1;
  const isCurrentYear = currentYear === new Date().getFullYear();

  if (clientInvoicesLoading) {
    return <LineChartSkeletonLoader />;
  }

  if (clientInvoicesError) {
    return (
      <div style={{ width: '100%', height: 542, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ padding: '20px', textAlign: 'center', color: '#6a7988' }}>
          Error loading data. Please try again.
        </div>
      </div>
    );
  }

  if (!processedData || processedData.length === 0) {
    return (
      <div style={{ width: '100%', height: 542, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ padding: '20px', textAlign: 'center', color: '#6a7988' }}>
          No data available
        </div>
      </div>
    );
  }

  // Calculate left margin based on Y-axis label length
  const calculateLeftMargin = () => {
    try {
      if (!processedData || processedData.length === 0) return 50;
      
      const maxValue = Math.max(
        ...processedData.map((item) => Math.max(item.currentYear || 0, item.lastYear || 0))
      );
      
      if (maxValue === 0 || isNaN(maxValue)) return 50;
      
      const formattedValue = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: currencies || "USD",
      }).format(maxValue);
      
      return Math.max(50, formattedValue.length * 8);
    } catch (error) {
      console.error('Error calculating left margin:', error);
      return 50;
    }
  };

  const leftMargin = calculateLeftMargin();

  return (
    <div style={{ width: '100%', height: 542 }}>
      <ResponsiveContainer>
        <LineChart
          layout="horizontal"
          data={processedData}
          margin={{
            top: 20,
            right: 30,
            left: leftMargin,
            bottom: 5,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" type="category" padding={{ left: 50 }} tickMargin={20} />
          <YAxis type="number" tickFormatter={(value) => new Intl.NumberFormat("en-US", { style: "currency", currency: currencies || "USD", }).format(value)} />
          <Tooltip formatter={(value) => `${new Intl.NumberFormat("en-US", { style: "currency", currency: currencies || "USD", }).format(value)}`} />
          <Legend />
          <Line dataKey="currentYear" name={isCurrentYear ? `Current Year (${currentYear})` : `Selected Year (${currentYear})`} stroke="#96A8BA" />
          <Line dataKey="lastYear" name={isCurrentYear ? `Last Year (${previousYear})` : `Previous Year (${previousYear})`} stroke="#6479F8" />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export default ReportClientComparionGraph;
